import { useEffect, useRef, useState, useCallback } from 'react';

export interface PoseHeadEstimate {
  /** Estimated yaw from ear/nose geometry (degrees, rough ±10°) */
  yaw: number;
  /** Estimated pitch (degrees, positive=up, negative=down) */
  pitch: number;
  /** Nose position in normalized [0,1] coords */
  noseX: number;
  noseY: number;
  /** Whether ears + nose were visible enough to estimate */
  valid: boolean;
}

interface PoseData {
  bodyVisible: boolean;
  shoulderAngle: number;
  head: PoseHeadEstimate;
  /** Raw 33-keypoint pose landmarks (with visibility) — exposed for
   *  PartialVisibilityClassifier to dispatch on eye/shoulder/nose presence.
   *  null when no pose detected. */
  rawLandmarks: Array<{ x: number; y: number; z?: number; visibility?: number }> | null;
}

/**
 * MediaPipe PoseLandmarker for body/shoulder detection.
 * Runs independently of WebEyeTracker (which handles face only).
 */
export function usePoseLandmarker(videoElementId: string) {
  const NO_HEAD: PoseHeadEstimate = { yaw: 0, pitch: 0, noseX: 0.5, noseY: 0.3, valid: false };
  const [poseData, setPoseData] = useState<PoseData>({ bodyVisible: false, shoulderAngle: 0, head: NO_HEAD, rawLandmarks: null });
  const poseLandmarkerRef = useRef<any>(null);
  const hasInitializedRef = useRef(false);
  const animFrameRef = useRef(0);

  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    let disposed = false;

    async function init() {
      try {
        const vision = await import('@mediapipe/tasks-vision');
        const { PoseLandmarker, FilesetResolver } = vision;

        const filesetResolver = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm'
        );

        const poseLandmarker = await PoseLandmarker.createFromOptions(filesetResolver, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
            delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          numPoses: 1,
        });

        if (disposed) return;
        poseLandmarkerRef.current = poseLandmarker;

        const videoEl = document.getElementById(videoElementId) as HTMLVideoElement;
        if (!videoEl) return;

        let lastDetectTime = 0;

        const detect = () => {
          if (disposed) return;
          animFrameRef.current = requestAnimationFrame(detect);

          const now = performance.now();
          // Throttle to ~10fps
          if (now - lastDetectTime < 100) return;
          if (!videoEl.videoWidth || videoEl.readyState < 2) return;
          lastDetectTime = now;

          try {
            const result = poseLandmarkerRef.current.detectForVideo(videoEl, now);
            if (result.landmarks && result.landmarks.length > 0) {
              const lm = result.landmarks[0];
              const lShoulder = lm[11];
              const rShoulder = lm[12];
              let shoulderAngle = 0;
              if (lShoulder && rShoulder) {
                const dy = rShoulder.y - lShoulder.y;
                const dx = rShoulder.x - lShoulder.x;
                shoulderAngle = Math.atan2(dy, dx) * (180 / Math.PI);
              }

              // Head pose from ear/nose geometry
              const nose = lm[0];
              const lEar = lm[7];
              const rEar = lm[8];
              let head: PoseHeadEstimate = NO_HEAD;

              if (nose && lEar && rEar) {
                const earDist = Math.sqrt(
                  (rEar.x - lEar.x) ** 2 + (rEar.y - lEar.y) ** 2,
                );
                if (earDist > 0.01) {
                  const earMidX = (lEar.x + rEar.x) / 2;
                  const earMidY = (lEar.y + rEar.y) / 2;
                  // Yaw: nose offset from ear midpoint (positive = right)
                  const yawFactor = (nose.x - earMidX) / earDist;
                  // Pitch: nose below ear center = looking down (negative in our convention)
                  const pitchFactor = (nose.y - earMidY) / earDist;
                  head = {
                    yaw: yawFactor * 80,
                    pitch: -pitchFactor * 80,
                    noseX: nose.x,
                    noseY: nose.y,
                    valid: true,
                  };
                }
              }

              setPoseData({ bodyVisible: true, shoulderAngle, head, rawLandmarks: lm });
            } else {
              setPoseData(prev => prev.bodyVisible
                ? { bodyVisible: false, shoulderAngle: 0, head: NO_HEAD, rawLandmarks: null }
                : prev);
            }
          } catch {
            // Ignore detection errors
          }
        }

        videoEl.addEventListener('loadeddata', () => {
          animFrameRef.current = requestAnimationFrame(detect);
        });
        if (videoEl.readyState >= 2) {
          animFrameRef.current = requestAnimationFrame(detect);
        }
      } catch (err) {
        console.warn('[usePoseLandmarker] Failed to initialize:', err);
      }
    }

    const timer = setTimeout(init, 1000);

    return () => {
      disposed = true;
      clearTimeout(timer);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [videoElementId]);

  return poseData;
}
